import {
  CookieConsent,
  CONSENT_COOKIE_NAME,
  CONSENT_EXPIRY_DAYS,
  CONSENT_VERSION
} from '@/types/cookie-consent'

export function setCookie(name: string, value: string, days: number): void {
  if (typeof window === 'undefined') return

  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)

  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax;Secure=${window.location.protocol === 'https:'}`
}

export function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null

  const nameEQ = name + '='
  const ca = document.cookie.split(';')

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

export function deleteCookie(name: string): void {
  if (typeof window === 'undefined') return
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`
}

export function saveConsentPreferences(consent: CookieConsent): void {
  const consentData = {
    ...consent,
    timestamp: Date.now(),
    version: CONSENT_VERSION
  }

  setCookie(
    CONSENT_COOKIE_NAME,
    JSON.stringify(consentData),
    CONSENT_EXPIRY_DAYS
  )

  // Store for audit purposes in localStorage as backup
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(
        `${CONSENT_COOKIE_NAME}-audit`,
        JSON.stringify({
          ...consentData,
          userAgent: navigator.userAgent,
          url: window.location.href
        })
      )
    } catch (error) {
      console.warn('Failed to store consent audit data:', error)
    }
  }
}

export function getConsentPreferences(): CookieConsent | null {
  const consentString = getCookie(CONSENT_COOKIE_NAME)

  if (!consentString) return null

  try {
    const consent = JSON.parse(consentString) as CookieConsent

    // Check if consent is still valid (version matches)
    if (consent.version !== CONSENT_VERSION) {
      deleteCookie(CONSENT_COOKIE_NAME)
      return null
    }

    // Validate that this is a proper consent decision (not just modal close)
    // If timestamp is missing or very recent without explicit choice, it might be invalid
    if (!consent.timestamp) {
      console.warn('Invalid consent data - missing timestamp')
      deleteCookie(CONSENT_COOKIE_NAME)
      return null
    }

    return consent
  } catch (error) {
    console.warn('Failed to parse consent preferences:', error)
    deleteCookie(CONSENT_COOKIE_NAME)
    return null
  }
}

export function hasValidConsent(): boolean {
  return getConsentPreferences() !== null
}

export function createDefaultConsent(): CookieConsent {
  return {
    essential: true,
    analytics: false,
    marketing: false,
    // preferences: false,
    timestamp: Date.now(),
    version: CONSENT_VERSION
  }
}

export function createAcceptAllConsent(): CookieConsent {
  return {
    essential: true,
    analytics: true,
    marketing: true,
    // preferences: true,
    timestamp: Date.now(),
    version: CONSENT_VERSION
  }
}

// Google Analytics and other tracking script management
export function loadGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined') return

  // Load gtag script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  // Initialize gtag
  window.dataLayer = window.dataLayer || []
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args)
  }

  gtag('js', new Date())
  gtag('config', measurementId, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=Lax;Secure'
  })
}

export function loadMarketingScripts(): void {
  if (typeof window === 'undefined') return

  // Add your marketing scripts here (Facebook Pixel, etc.)
  // console.log('Loading marketing scripts...')
}

export function removeTrackingScripts(): void {
  if (typeof window === 'undefined') return

  // Remove Google Analytics
  const gaScripts = document.querySelectorAll(
    'script[src*="googletagmanager.com"]'
  )
  gaScripts.forEach(script => script.remove())

  // Clear dataLayer
  if (window.dataLayer) {
    window.dataLayer.length = 0
  }

  // Remove marketing scripts
  // console.log('Removing tracking scripts...')
}

export function clearAllConsentData(): void {
  if (typeof window === 'undefined') return

  // Remove consent cookie
  deleteCookie(CONSENT_COOKIE_NAME)

  // Remove localStorage audit data
  try {
    localStorage.removeItem(`${CONSENT_COOKIE_NAME}-audit`)
    localStorage.removeItem('waltz-ai-consent-history')
  } catch (error) {
    console.warn('Failed to clear localStorage consent data:', error)
  }

  // console.log('🧹 All consent data cleared')
}

declare global {
  interface Window {
    dataLayer: unknown[]
  }
}
